import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { IntlProvider } from 'react-intl';
import {
  messages,
  defaultLocale,
  SupportedLocale,
  supportedLocales,
} from '../locales';

interface LocaleContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

interface LocaleProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'absolvire-app-locale';

// Helper function to get locale from localStorage
const getStoredLocale = (): SupportedLocale => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && supportedLocales.includes(stored as SupportedLocale)) {
      return stored as SupportedLocale;
    }
  } catch (error) {
    console.warn('Failed to read locale from localStorage:', error);
  }
  return defaultLocale;
};

// Helper function to save locale to localStorage
const saveLocaleToStorage = (locale: SupportedLocale): void => {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch (error) {
    console.warn('Failed to save locale to localStorage:', error);
  }
};

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<SupportedLocale>(getStoredLocale);

  // Save to localStorage whenever locale changes
  useEffect(() => {
    saveLocaleToStorage(locale);
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <IntlProvider locale={locale} messages={messages[locale]}>
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
};
